import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { getPerformance } from '@jira/ssr__browser-globals/src/getPerformance.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';

type WindowStatsigValues = { _statsigValues?: Record<string, unknown> };

const hasStatsigValues = (
	value: Window | typeof globalThis,
): value is (Window | typeof globalThis) & WindowStatsigValues => '_statsigValues' in value;

const valuesFromGlobal = (() => {
	const globalVar = getWindow() ?? globalThis;
	return {
		locator: () => hasStatsigValues(globalVar),
		processor: () => (hasStatsigValues(globalVar) ? globalVar._statsigValues : undefined),
	};
})();

const valuesFromMeta = (() => {
	// If parsing fails once, it will always fail. Avoid overhead of parsing again & again.
	let parsingFailed = false;

	const locator = () => {
		if (parsingFailed) {
			return null;
		}

		const statsigValues = getMeta('ajs-fe-statsig-values');

		try {
			const statsigValues2 = getMeta('ajs-fe-statsig-values-2');
			if (statsigValues && statsigValues2) {
				const statsigValuesObject = JSON.parse(statsigValues);
				const statsigValues2Object = JSON.parse(statsigValues2);

				Object.assign(statsigValuesObject.feature_gates, statsigValues2Object);
				return JSON.stringify(statsigValuesObject);
			}

			return statsigValues;
		} catch (e) {
			return statsigValues;
		}
	};

	return {
		locator,
		processor: () => {
			if (parsingFailed) {
				throw new Error("Can't parse 'ajs-fe-statsig-values' value");
			}

			const featureFlags = locator();
			if (featureFlags != null && featureFlags !== '') {
				getPerformance()?.mark?.('statsig-parse:start');
				try {
					const result = JSON.parse(featureFlags);
					getPerformance()?.mark?.('statsig-parse:end');
					return result;
				} catch {
					parsingFailed = true;
					throw new Error("Can't parse 'ajs-fe-statsig-values' value");
				}
			}
		},
	};
})();

const _haveValuesArrived = (): boolean => {
	return Boolean(valuesFromGlobal.locator() || valuesFromMeta.locator());
};

export const getGateValues = (): Record<string, unknown> | undefined => {
	if (valuesFromGlobal.locator()) {
		return valuesFromGlobal.processor();
	}
	if (valuesFromMeta.locator()) {
		return valuesFromMeta.processor();
	}
	return undefined;
};
